import React, { useState, useEffect, useRef } from 'react';
import { Container, Form, OverlayTrigger, Tooltip, Modal } from 'react-bootstrap';
import axios from 'axios';
import moment from 'moment';
import OrgTableShimmer from "ATSComponents/Common/OrgTableShimmer";
import { triggerAlert } from 'utils/CommonFunctions';
import CompanySelector from 'ATSComponents/Common/CompanySelector';

/* ═══════════════════════════════════════════════════════════════
   INLINE SVG ICONS
   ═══════════════════════════════════════════════════════════════ */

const MailIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="4" width="20" height="16" rx="2" />
    <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
  </svg>
);

const SendIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="m22 2-7 20-4-9-9-4Z" /><path d="m22 2-11 11" />
  </svg>
);

const CalendarIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
    <line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);

const RefreshIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 2v6h-6" /><path d="M3 12a9 9 0 0 1 15-6.7L21 8" />
    <path d="M3 22v-6h6" /><path d="M21 12a9 9 0 0 1-15 6.7L3 16" />
  </svg>
);

const CloseIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const SearchIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" />
  </svg>
);

const ChevronLeftIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="m15 18-6-6 6-6" />
  </svg>
);

const ChevronRightIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="m9 18 6-6-6-6" />
  </svg>
);

const InfoIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" /><line x1="12" y1="16" x2="12" y2="12" /><line x1="12" y1="8" x2="12.01" y2="8" />
  </svg>
);

const AttachIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l8.57-8.57A4 4 0 1 1 18 8.84l-8.59 8.57a2 2 0 0 1-2.83-2.83l8.49-8.48" />
  </svg>
);

const EmptyIcon = () => (
  <svg width="52" height="52" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="0.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 12h-6l-2 3h-4l-2-3H2" />
    <path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11Z" />
  </svg>
);

/* ═══════════════════════════════════════════════════════════════
   HELPERS
   ═══════════════════════════════════════════════════════════════ */

const AVATAR_COLORS = [
  '#0078d4','#005a9e','#107c10','#ca5010',
  '#8764b8','#038387','#e3008c','#4f6bed',
];
const stringToColor = (s = '') => {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = s.charCodeAt(i) + ((h << 5) - h);
  return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length];
};
const getInitials = (s = '') => {
  const p = s.split('@')[0].split(/[.\s_-]+/).filter(Boolean);
  if (!p.length) return '?';
  if (p.length === 1) return p[0].slice(0, 2).toUpperCase();
  return (p[0][0] + p[1][0]).toUpperCase();
};
const toEmailList = (v) => {
  if (Array.isArray(v)) return v.filter(Boolean);
  if (typeof v === 'string') return v.split(',').map(e => e.trim()).filter(Boolean);
  return [];
};
const relativeTime = (dateStr) => {
  if (!dateStr) return '';
  const d = moment(dateStr);
  const now = moment();
  if (d.isSame(now, 'day')) return d.format('h:mm A');
  if (d.isSame(now.clone().subtract(1, 'day'), 'day')) return 'Yesterday';
  if (d.isAfter(now.clone().subtract(7, 'days'))) return d.format('ddd');
  if (d.isSame(now, 'year')) return d.format('MMM D');
  return d.format('MMM D, YYYY');
};

/* strip html tags for preview snippet */
const stripHtml = (html = '') => {
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  return (tmp.textContent || tmp.innerText || '').replace(/\s+/g, ' ').trim();
};

/* ═══════════════════════════════════════════════════════════════
   MAIN COMPONENT
   ═══════════════════════════════════════════════════════════════ */

const SentEmails = () => {
  document.title = 'ATS - Sent Emails';

  const userInfos       = JSON.parse(sessionStorage.getItem('atsuser-info'));
  const today           = moment().format('YYYY-MM-DD');
  const initialCompanyId = userInfos.user_details.companies_data[0].company_id;

  const [dateFrom,       setDateFrom]       = useState(today);
  const [dateTo,         setDateTo]         = useState(today);
  const [companyId,      setCompanyId]      = useState(initialCompanyId);
  const [tableData,      setTableData]      = useState([]);
  const [loading,        setLoading]        = useState(false);
  const [page,           setPage]           = useState(1);
  const [perPage]                           = useState(50); // load more per page for inbox feel
  const [totalRows,      setTotalRows]      = useState(0);
  const [selectedEmail,  setSelectedEmail]  = useState(null);
  const [searchQuery,    setSearchQuery]    = useState('');

  const previewRef = useRef(null);

  /* filtered list based on search */
  const filteredData = searchQuery.trim()
    ? tableData.filter(e => {
        const q = searchQuery.toLowerCase();
        return (
          (e.fromName      || '').toLowerCase().includes(q) ||
          (e.subject       || '').toLowerCase().includes(q) ||
          (e.createdBy     || '').toLowerCase().includes(q) ||
          toEmailList(e.toDisplayName).some(t => t.toLowerCase().includes(q))
        );
      })
    : tableData;

  /* ── Fetch ── */
  const fetchEmails = async (pg) => {
    try {
      setLoading(true);
      let url = `/api/v1/emails/?page=${pg}&size=${perPage}&company_id=${companyId}`;
      if (dateFrom) url += `&date_from=${dateFrom}`;
      if (dateTo)   url += `&date_to=${dateTo}`;

      const response = await axios.get(url);
      setTotalRows(response.data.count);

      const formatted = response.data.results.map((email, i) => ({
        id:            email.email_id || i + 1,
        fromName:      email.from_address   || '',
        toDisplayName: email.to_addresses   || '',
        subject:       email.subject        || '(No subject)',
        date:          email.created_at ? moment(email.created_at).format('YYYY-MM-DD HH:mm') : '',
        createdBy:     email.created_by_name || '',
        body:          email.body           || '',
        snippet:       stripHtml(email.body || ''),
        ...email,
      }));

      setTableData(formatted);
      if (formatted.length && !selectedEmail) setSelectedEmail(formatted[0]);
    } catch (err) {
      console.error(err);
      triggerAlert('error', 'Error', 'Failed to fetch Emails');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchEmails(page); }, [page, companyId, dateFrom, dateTo]);

  /* ── Neutralise interactive elements in preview ── */
  useEffect(() => {
    if (!selectedEmail || !previewRef.current) return;
    const container   = previewRef.current;
    const interactive = container.querySelectorAll('a,button,input,select,textarea,form,[onclick],[onsubmit]');
    const stop = e => { e.preventDefault(); e.stopPropagation(); };
    interactive.forEach(el => {
      el.removeAttribute('onclick');
      el.removeAttribute('onsubmit');
      if (el.tagName === 'A')    el.removeAttribute('href');
      if (el.tagName === 'FORM') el.setAttribute('action', 'javascript:void(0)');
      el.setAttribute('tabindex', '-1');
      el.setAttribute('aria-disabled', 'true');
      el.style.pointerEvents = 'none';
      el.style.opacity       = '0.55';
      el.style.cursor        = 'not-allowed';
      if ('disabled' in el) el.disabled = true;
      el.addEventListener('click',  stop, true);
      el.addEventListener('submit', stop, true);
    });
  }, [selectedEmail]);

  const handleCompanyChange = c => { if (c?.company_id) { setCompanyId(c.company_id); setPage(1); setSelectedEmail(null); }};
  const handleResetFilters  = () => { setDateFrom(today); setDateTo(today); setPage(1); setSelectedEmail(null); };

  const totalPages = Math.ceil(totalRows / perPage);

  /* ═══════════════════════════════════════════════════════════════
     RENDER
     ═══════════════════════════════════════════════════════════════ */
  return (
    <div className="page-content allact-tabs manager-dash se-root">
      <Container fluid className="se-container">

        {/* ── Top Bar ── */}
        <div className="se-topbar">
          <div className="se-topbar-brand">
            <div className="se-topbar-icon"><MailIcon /></div>
            <div>
              <h4 className="se-topbar-title">Sent</h4>
              <p className="se-topbar-sub">Outgoing email history</p>
            </div>
          </div>

          <div className="se-topbar-controls">
            {/* Search */}
            {/* Date filters */}
            <div className="se-date-group">
              <label className="se-date-label"><CalendarIcon /> From</label>
              <input type="date" className="se-date-input" value={dateFrom}
                onChange={e => { setDateFrom(e.target.value); setPage(1); setSelectedEmail(null); }} />
            </div>
            <div className="se-date-group">
              <label className="se-date-label"><CalendarIcon /> To</label>
              <input type="date" className="se-date-input" value={dateTo}
                onChange={e => { setDateTo(e.target.value); setPage(1); setSelectedEmail(null); }} />
            </div>
            <button className="se-reset-btn" onClick={handleResetFilters} title="Reset to today">
              <RefreshIcon /> Reset
            </button>

            {/* Company */}
            <div className="se-company-wrap">
              <label className="se-company-label">Company</label>
              <div className="se-company-sel">
                <CompanySelector onCompanyChange={handleCompanyChange} />
              </div>
            </div>
          </div>
        </div>

        {/* ── Inbox Shell ── */}
        <div className="se-inbox-shell">

          {/* ── LEFT: email list panel ── */}
          <div className="se-list-panel">
            {/* list header */}
            <div className="se-list-header">
              <span className="se-list-count">
                {loading ? 'Loading…' : `${filteredData.length} of ${totalRows}`}
              </span>
              <div className="se-list-pager">
                <button className="se-pager-btn" disabled={page <= 1} onClick={() => setPage(p => p - 1)} title="Previous page">
                  <ChevronLeftIcon />
                </button>
                <span className="se-pager-info">{page} / {totalPages || 1}</span>
                <button className="se-pager-btn" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} title="Next page">
                  <ChevronRightIcon />
                </button>
              </div>
            </div>

            {/* list body */}
            <div className="se-list-body">
              {loading ? (
                <div className="se-list-shimmer">
                  {[...Array(8)].map((_, i) => (
                    <div key={i} className="se-shimmer-row">
                      <div className="se-shimmer-avatar" />
                      <div className="se-shimmer-lines">
                        <div className="se-shimmer-line se-shimmer-title" />
                        <div className="se-shimmer-line se-shimmer-sub" />
                        <div className="se-shimmer-line se-shimmer-preview" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : filteredData.length === 0 ? (
                <div className="se-list-empty">
                  <EmptyIcon />
                  <p className="se-empty-title">No emails found</p>
                  <p className="se-empty-sub">Try adjusting your date range or search.</p>
                </div>
              ) : (
                filteredData.map(email => {
                  const isSelected = selectedEmail?.id === email.id;
                  const toList     = toEmailList(email.toDisplayName);
                  return (
                    <div
                      key={email.id}
                      className={`se-email-row${isSelected ? ' se-email-row--selected' : ''}`}
                      onClick={() => setSelectedEmail(email)}
                    >
                      {/* selection indicator */}
                      <div className="se-row-indicator" />

                      {/* avatar */}
                      <div
                        className="se-row-avatar"
                        style={{ backgroundColor: stringToColor(email.fromName) }}
                      >
                        {getInitials(email.fromName)}
                      </div>

                      {/* text */}
                      <div className="se-row-text">
                        <div className="se-row-top">
                          <span className="se-row-from" title={email.fromName}>
                            {email.fromName || '(Unknown)'}
                          </span>
                          <span className="se-row-time">{relativeTime(email.date)}</span>
                        </div>
                        <div className="se-row-subject" title={email.subject}>
                          <SendIcon /> {email.subject}
                        </div>
                        <div className="se-row-to" title={toList.join(', ')}>
                          To: {toList.slice(0, 2).join(', ')}{toList.length > 2 ? ` +${toList.length - 2}` : ''}
                        </div>
                        {email.snippet && (
                          <div className="se-row-snippet">{email.snippet.slice(0, 100)}</div>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* ── RIGHT: reading pane ── */}
          <div className="se-reading-pane">
            {!selectedEmail ? (
              <div className="se-pane-empty">
                <div className="se-pane-empty-icon"><MailIcon /></div>
                <p className="se-pane-empty-title">Select an email to read</p>
                <p className="se-pane-empty-sub">Choose a message from the list on the left.</p>
              </div>
            ) : (
              <>
                {/* pane header */}
                <div className="se-pane-header">
                  <h3 className="se-pane-subject">{selectedEmail.subject}</h3>
                  <div className="se-pane-meta-row">
                    <div
                      className="se-pane-avatar"
                      style={{ backgroundColor: stringToColor(selectedEmail.fromName) }}
                    >
                      {getInitials(selectedEmail.fromName)}
                    </div>
                    <div className="se-pane-meta-info">
                      <div className="se-pane-from">{selectedEmail.fromName || '—'}</div>
                      <div className="se-pane-to">
                        <span className="se-pane-to-label">To:</span>
                        {toEmailList(selectedEmail.toDisplayName).join(', ') || '—'}
                      </div>
                    </div>
                    <div className="se-pane-meta-right">
                      <div className="se-pane-date">
                        {selectedEmail.date ? moment(selectedEmail.date).format('ddd, DD MMM YYYY, h:mm A') : '—'}
                      </div>
                      {selectedEmail.createdBy && (
                        <div className="se-pane-sentby">Sent by {selectedEmail.createdBy}</div>
                      )}
                    </div>
                  </div>
                </div>

                {/* security banner */}
                <div className="se-security-banner">
                  <InfoIcon />
                  <span>Links, buttons, and forms in this preview are disabled for security.</span>
                </div>

                {/* email body */}
                <div className="se-pane-body-wrap">
                  <div
                    ref={previewRef}
                    className="se-pane-body"
                    dangerouslySetInnerHTML={{ __html: selectedEmail.body }}
                  />
                </div>
              </>
            )}
          </div>
        </div>
      </Container>

      {/* ═══════════════════════════════════════════════════════════════
         STYLES
         ═══════════════════════════════════════════════════════════════ */}
      <style>{`
        /* ── Root ── */
        .se-root {
          background: #f3f2f1 !important;
          min-height: 100vh;
        }
        .se-container {
          padding: 16px 20px 0;
          max-width: 1600px;
          display: flex;
          flex-direction: column;
          height: calc(100vh - 60px);
        }

        /* ── Top Bar ── */
        .se-topbar {
          display: flex;
          align-items: center;
          justify-content: space-between;
          background: #fff;
          border-radius: 10px 10px 0 0;
          padding: 12px 20px;
          border-bottom: 1px solid #edebe9;
          flex-shrink: 0;
          gap: 12px;
          flex-wrap: wrap;
          box-shadow: 0 1px 3px rgba(0,0,0,0.06);
        }
        .se-topbar-brand {
          display: flex;
          align-items: center;
          gap: 12px;
        }
        .se-topbar-icon {
          width: 38px;
          height: 38px;
          border-radius: 9px;
          background: #0078d4;
          color: #fff;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }
        .se-topbar-title {
          font-size: 1.1rem;
          font-weight: 700;
          color: #201f1e;
          margin: 0;
          line-height: 1.2;
        }
        .se-topbar-sub {
          font-size: 0.73rem;
          color: #a19f9d;
          margin: 1px 0 0;
        }
        .se-topbar-controls {
          display: flex;
          align-items: center;
          gap: 10px;
          flex-wrap: wrap;
        }

        /* search */
        .se-search-wrap {
          position: relative;
          flex-shrink: 0;
        }
        .se-search-ico {
          position: absolute;
          left: 9px;
          top: 50%;
          transform: translateY(-50%);
          color: #a19f9d;
          display: flex;
          pointer-events: none;
        }
        .se-search {
          width: 210px;
          height: 32px;
          border: 1px solid #d2d0ce;
          border-radius: 6px;
          padding: 0 10px 0 30px;
          font-size: 0.8rem;
          color: #323130;
          background: #faf9f8;
          transition: border-color 0.15s, box-shadow 0.15s;
          outline: none;
        }
        .se-search:focus {
          border-color: #0078d4;
          box-shadow: 0 0 0 1px #0078d4;
          background: #fff;
        }

        /* date */
        .se-date-group {
          display: flex;
          flex-direction: column;
          gap: 1px;
        }
        .se-date-label {
          font-size: 0.65rem;
          font-weight: 700;
          color: #a19f9d;
          text-transform: uppercase;
          letter-spacing: 0.04em;
          display: flex;
          align-items: center;
          gap: 3px;
          margin: 0;
        }
        .se-date-input {
          height: 32px;
          width: 148px;
          border: 1px solid #d2d0ce;
          border-radius: 6px;
          padding: 0 8px;
          font-size: 0.78rem;
          color: #323130;
          background: #faf9f8;
          outline: none;
          transition: border-color 0.15s;
        }
        .se-date-input:focus { border-color: #0078d4; box-shadow: 0 0 0 1px #0078d4; }

        /* reset */
        .se-reset-btn {
          display: inline-flex;
          align-items: center;
          gap: 5px;
          height: 32px;
          padding: 0 12px;
          font-size: 0.75rem;
          font-weight: 600;
          color: #0078d4;
          background: #eff6ff;
          border: 1px solid #c7e0f4;
          border-radius: 6px;
          cursor: pointer;
          transition: all 0.15s;
          white-space: nowrap;
          align-self: flex-end;
        }
        .se-reset-btn:hover { background: #deecf9; border-color: #0078d4; }

        /* company */
        .se-company-wrap {
          display: flex;
          flex-direction: column;
          gap: 1px;
        }
        .se-company-label {
          font-size: 0.65rem;
          font-weight: 700;
          color: #a19f9d;
          text-transform: uppercase;
          letter-spacing: 0.04em;
          margin: 0;
        }
        .se-company-sel { width: 190px; }

        /* ── Inbox Shell ── */
        .se-inbox-shell {
          display: flex;
          flex: 1;
          min-height: 0;
          background: #fff;
          border-radius: 0 0 10px 10px;
          overflow: hidden;
          box-shadow: 0 2px 8px rgba(0,0,0,0.08);
          margin-bottom: 20px;
        }

        /* ── List Panel ── */
        .se-list-panel {
          width: 340px;
          flex-shrink: 0;
          border-right: 1px solid #edebe9;
          display: flex;
          flex-direction: column;
          overflow: hidden;
          background: #fafafa;
        }
        .se-list-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 10px 14px;
          border-bottom: 1px solid #edebe9;
          background: #f3f2f1;
          flex-shrink: 0;
        }
        .se-list-count {
          font-size: 0.72rem;
          font-weight: 600;
          color: #605e5c;
        }
        .se-list-pager {
          display: flex;
          align-items: center;
          gap: 4px;
        }
        .se-pager-btn {
          width: 26px;
          height: 26px;
          border-radius: 5px;
          border: 1px solid #d2d0ce;
          background: #fff;
          color: #323130;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.15s;
        }
        .se-pager-btn:hover:not(:disabled) { background: #e1dfdd; }
        .se-pager-btn:disabled { opacity: 0.35; cursor: not-allowed; }
        .se-pager-info { font-size: 0.7rem; color: #605e5c; font-weight: 600; padding: 0 2px; }

        .se-list-body {
          flex: 1;
          overflow-y: auto;
          scroll-behavior: smooth;
        }
        .se-list-body::-webkit-scrollbar { width: 5px; }
        .se-list-body::-webkit-scrollbar-thumb { background: #d2d0ce; border-radius: 4px; }
        .se-list-body::-webkit-scrollbar-track { background: transparent; }

        /* email row */
        .se-email-row {
          display: flex;
          align-items: flex-start;
          gap: 10px;
          padding: 12px 14px 10px 0;
          cursor: pointer;
          border-bottom: 1px solid #f3f2f1;
          transition: background 0.12s;
          position: relative;
          background: #fafafa;
        }
        .se-email-row:hover { background: #f0f6ff; }
        .se-email-row--selected { background: #eff6ff !important; }

        /* left accent bar */
        .se-row-indicator {
          width: 3px;
          border-radius: 0 3px 3px 0;
          align-self: stretch;
          flex-shrink: 0;
          background: transparent;
          transition: background 0.15s;
        }
        .se-email-row--selected .se-row-indicator { background: #0078d4; }

        .se-row-avatar {
          width: 36px;
          height: 36px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 0.65rem;
          font-weight: 700;
          color: #fff;
          flex-shrink: 0;
          letter-spacing: 0.02em;
        }
        .se-row-text { flex: 1; min-width: 0; }
        .se-row-top {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 2px;
          gap: 6px;
        }
        .se-row-from {
          font-size: 0.82rem;
          font-weight: 700;
          color: #201f1e;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          flex: 1;
        }
        .se-row-time {
          font-size: 0.68rem;
          color: #a19f9d;
          flex-shrink: 0;
          white-space: nowrap;
        }
        .se-email-row--selected .se-row-time { color: #0078d4; }

        .se-row-subject {
          display: flex;
          align-items: center;
          gap: 5px;
          font-size: 0.78rem;
          color: #323130;
          font-weight: 600;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          margin-bottom: 2px;
        }
        .se-row-subject svg { color: #0078d4; flex-shrink: 0; opacity: 0.7; }

        .se-row-to {
          font-size: 0.7rem;
          color: #a19f9d;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          margin-bottom: 2px;
        }
        .se-row-snippet {
          font-size: 0.7rem;
          color: #a19f9d;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        .se-email-row--selected .se-row-snippet { color: #605e5c; }

        /* shimmer */
        .se-list-shimmer { padding: 6px 0; }
        .se-shimmer-row {
          display: flex;
          gap: 10px;
          padding: 12px 14px;
          border-bottom: 1px solid #f3f2f1;
          animation: se-shimmer 1.4s ease infinite;
        }
        .se-shimmer-avatar {
          width: 36px;
          height: 36px;
          border-radius: 50%;
          background: #e1dfdd;
          flex-shrink: 0;
        }
        .se-shimmer-lines { flex: 1; display: flex; flex-direction: column; gap: 6px; justify-content: center; }
        .se-shimmer-line { height: 10px; border-radius: 4px; background: linear-gradient(90deg, #e1dfdd 25%, #f3f2f1 50%, #e1dfdd 75%); background-size: 200% 100%; animation: se-shimmer-bg 1.4s ease infinite; }
        .se-shimmer-title { width: 60%; }
        .se-shimmer-sub   { width: 80%; }
        .se-shimmer-preview { width: 90%; opacity: 0.6; }
        @keyframes se-shimmer-bg { 0%{background-position:200% 0} 100%{background-position:-200% 0} }

        /* list empty */
        .se-list-empty {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 60px 20px;
          text-align: center;
          color: #c8c6c4;
        }
        .se-empty-title { font-size: 0.9rem; font-weight: 700; color: #323130; margin: 12px 0 4px; }
        .se-empty-sub   { font-size: 0.78rem; color: #a19f9d; margin: 0; }

        /* ── Reading Pane ── */
        .se-reading-pane {
          flex: 1;
          min-width: 0;
          display: flex;
          flex-direction: column;
          overflow: hidden;
          background: #fff;
        }

        /* pane empty state */
        .se-pane-empty {
          flex: 1;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          color: #c8c6c4;
          text-align: center;
          padding: 40px;
        }
        .se-pane-empty-icon {
          width: 64px;
          height: 64px;
          border-radius: 50%;
          background: #f3f2f1;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 16px;
          color: #c8c6c4;
        }
        .se-pane-empty-title { font-size: 1rem; font-weight: 700; color: #323130; margin: 0 0 6px; }
        .se-pane-empty-sub   { font-size: 0.82rem; color: #a19f9d; margin: 0; }

        /* pane header */
        .se-pane-header {
          padding: 20px 28px 16px;
          border-bottom: 1px solid #f3f2f1;
          flex-shrink: 0;
        }
        .se-pane-subject {
          font-size: 1.15rem;
          font-weight: 700;
          color: #201f1e;
          margin: 0 0 14px;
          line-height: 1.3;
        }
        .se-pane-meta-row {
          display: flex;
          align-items: flex-start;
          gap: 12px;
        }
        .se-pane-avatar {
          width: 42px;
          height: 42px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 0.78rem;
          font-weight: 700;
          color: #fff;
          flex-shrink: 0;
        }
        .se-pane-meta-info { flex: 1; min-width: 0; }
        .se-pane-from {
          font-size: 0.9rem;
          font-weight: 700;
          color: #201f1e;
          line-height: 1.3;
        }
        .se-pane-to {
          font-size: 0.78rem;
          color: #605e5c;
          margin-top: 2px;
          word-break: break-word;
        }
        .se-pane-to-label {
          font-weight: 700;
          color: #a19f9d;
          margin-right: 4px;
        }
        .se-pane-meta-right {
          flex-shrink: 0;
          text-align: right;
        }
        .se-pane-date {
          font-size: 0.75rem;
          color: #605e5c;
          white-space: nowrap;
        }
        .se-pane-sentby {
          font-size: 0.7rem;
          color: #a19f9d;
          margin-top: 3px;
        }

        /* security banner */
        .se-security-banner {
          display: flex;
          align-items: center;
          gap: 7px;
          background: #fff4ce;
          border-bottom: 1px solid #f3d577;
          color: #835c00;
          padding: 7px 28px;
          font-size: 0.73rem;
          font-weight: 500;
          flex-shrink: 0;
        }

        /* body */
        .se-pane-body-wrap {
          flex: 1;
          overflow-y: auto;
          padding: 24px 28px 32px;
        }
        .se-pane-body-wrap::-webkit-scrollbar { width: 6px; }
        .se-pane-body-wrap::-webkit-scrollbar-thumb { background: #d2d0ce; border-radius: 4px; }
        .se-pane-body-wrap::-webkit-scrollbar-track { background: transparent; }

        .se-pane-body {
          font-size: 14px;
          line-height: 1.65;
          color: #323130;
          word-break: break-word;
        }
        .se-pane-body img { max-width: 100%; height: auto; }

        /* ── Responsive ── */
        @media (max-width: 900px) {
          .se-inbox-shell { flex-direction: column; }
          .se-list-panel { width: 100%; max-height: 340px; border-right: none; border-bottom: 1px solid #edebe9; }
          .se-topbar { flex-direction: column; align-items: flex-start; }
          .se-topbar-controls { flex-wrap: wrap; }
        }
      `}</style>
    </div>
  );
};

export default SentEmails;